import type { ReactNode } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/lib/utils'

export function SectionCard({
  title,
  icon,
  description,
  action,
  children,
  className,
  contentClassName,
  actionClassName,
  noPad,
}: {
  title?: ReactNode
  icon?: ReactNode
  description?: ReactNode
  action?: ReactNode
  children: ReactNode
  className?: string
  contentClassName?: string
  actionClassName?: string
  noPad?: boolean
}) {
  return (
    <Card
      className={cn(
        'group/section relative rounded-2xl border-border/60 bg-card',
        'shadow-[0_1px_2px_color-mix(in_oklab,var(--shadow-soft)_4%,transparent),0_4px_12px_-2px_color-mix(in_oklab,var(--shadow-soft)_6%,transparent),0_16px_32px_-12px_color-mix(in_oklab,var(--shadow-soft)_8%,transparent)]',
        'transition-[box-shadow,border-color] duration-300 ease-out',
        'hover:border-border hover:shadow-[0_1px_2px_color-mix(in_oklab,var(--shadow-soft)_5%,transparent),0_8px_20px_-4px_color-mix(in_oklab,var(--shadow-soft)_9%,transparent),0_24px_48px_-16px_color-mix(in_oklab,var(--shadow-soft)_14%,transparent)]',
        'dark:shadow-[inset_0_1px_0_color-mix(in_oklab,var(--white)_4%,transparent),0_8px_24px_-8px_color-mix(in_oklab,var(--black)_50%,transparent)]',
        'dark:hover:shadow-[inset_0_1px_0_color-mix(in_oklab,var(--white)_6%,transparent),0_12px_32px_-8px_color-mix(in_oklab,var(--black)_65%,transparent)]',
        className,
      )}
    >
      {title ? (
        <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 space-y-0 p-5 pb-4">
          <div className="flex min-w-0 items-start gap-3">
            {icon ? (
              <span
                className={cn(
                  'flex size-9 shrink-0 items-center justify-center rounded-xl text-primary [&_svg]:size-[18px]',
                  'bg-gradient-to-br from-primary/15 to-primary/5 ring-1 ring-inset ring-primary/15',
                  'shadow-[0_2px_6px_-1px_color-mix(in_oklch,var(--primary)_25%,transparent)]',
                  'transition-transform duration-300 ease-out group-hover/section:scale-105',
                )}
              >
                {icon}
              </span>
            ) : null}
            <div className="min-w-0 space-y-1">
              <CardTitle className={cn('text-base font-semibold tracking-tight', icon && 'leading-9')}>{title}</CardTitle>
              {description ? <CardDescription className="text-[13px]">{description}</CardDescription> : null}
            </div>
          </div>
          {action ? <div className={cn('max-w-full', actionClassName)}>{action}</div> : null}
        </CardHeader>
      ) : null}
      <CardContent className={cn(noPad && 'px-0 pb-0', !title && 'p-5', title && 'px-2 pt-0 pb-5', contentClassName)}>
        {children}
      </CardContent>
    </Card>
  )
}

export function KvRow({ label, value, className }: { label: string; value: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'grid grid-cols-[minmax(6.5rem,min(42%,11rem))_minmax(0,1fr)] items-start gap-3 border-b border-border/50 py-2.5 last:border-0',
        className,
      )}
    >
      <span className="text-[13px] text-muted-foreground">{label}</span>
      <span className="min-w-0 break-words text-[13px] font-medium">{value}</span>
    </div>
  )
}
